<template>
    <div class="container-fluid px-5 mt-4">

        <!-- Nadpis stránky -->
        <div class="d-flex align-items-center justify-content-between mb-4 certs-page-header">
            <h1 class="credits-page__title">{{ $t('cert.certificates') }}</h1>
            <div class="d-flex gap-2">
                <button class="btn certs-add-folder-btn" @click="showAddFolderModal = true">
                    {{ $t('cert.addFolder') }}
                    <img :src="iconFolder" alt="" class="certs-add-folder-btn__icon">
                </button>
                <router-link :to="{ name: 'CertCreate' }" class="btn btn-primary" style="height:40px;padding:0 16px;font-size:14px;font-weight:500;">
                    {{ $t('cert.certCreate') }}
                </router-link>
            </div>
        </div>

        <!-- Načítání prvního zobrazení -->
        <Skeletor v-if="loading && baseCount === null" />

        <!-- Plný empty state — žádné certifikáty ani složky -->
        <div v-if="showFullEmpty" class="d-flex justify-content-center mt-5 pt-4">
            <EmptyStateComponent
                :icon="iconSpecialOk"
                :icon-style="{ width: '17px', height: '17px', filter: 'brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)' }"
                :title="$t('cert.emptyCertsTitle')"
                :text="$t('cert.emptyCertsText')"
            >
                <router-link :to="{ name: 'CertCreate' }" class="btn btn-primary app-nav__create-btn">
                    {{ $t('cert.certCreate') }} <img :src="iconPlus" alt="">
                </router-link>
            </EmptyStateComponent>
        </div>

        <!-- Modal: Přidat složku -->
        <div v-if="showAddFolderModal" class="create-cert-overlay" @click.self="showAddFolderModal = false">
            <div class="create-cert-modal" style="max-width:400px;">
                <div class="create-cert-modal__header">
                    <span class="create-cert-modal__title">{{ $t('cert.addFolder') }}</span>
                    <button class="create-cert-modal__close" @click="showAddFolderModal = false">×</button>
                </div>
                <div class="create-cert-modal__body">
                    <label class="create-cert-label mb-1">{{ $t('file.name') }}</label>
                    <input
                        type="text"
                        class="form-control mb-4"
                        v-model="newFolderName"
                        :placeholder="$t('file.newFolder')"
                    >
                    <div class="d-flex justify-content-end">
                        <button class="btn btn-primary" @click="createFolder" :disabled="!newFolderName || creatingFolder">
                            <span v-if="creatingFolder" class="spinner-border spinner-border-sm me-1" role="status"></span>
                            {{ $t('file.create') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <template v-if="!showFullEmpty">

        <!-- Modal: Přesunout certifikát -->
        <div v-if="showMoveModal" class="create-cert-overlay" @click.self="showMoveModal = false">
            <div class="create-cert-modal" style="max-width:400px;">
                <div class="create-cert-modal__header">
                    <span class="create-cert-modal__title">{{ $t('cert.moveToFolder') }}</span>
                    <button class="create-cert-modal__close" @click="showMoveModal = false">×</button>
                </div>
                <div class="create-cert-modal__body">
                    <p class="create-cert-label mb-3">{{ moveTargetCert?.name }}</p>
                    <div v-if="moveFoldersLoading" class="text-center py-3">
                        <span class="spinner-border spinner-border-sm" role="status"></span>
                    </div>
                    <div v-else>
                        <div v-if="!moveFolders.length" class="text-muted text-center py-2" style="font-size:.875rem;">
                            {{ $t('cert.noFolders') }}
                        </div>
                        <div
                            v-for="folder in moveFolders"
                            :key="folder.uuid"
                            class="move-folder-item"
                            :class="{ 'move-folder-item--moving': movingToFolder === folder.uuid }"
                            :style="{ paddingLeft: (12 + folder.depth * 20) + 'px' }"
                            @click="movingToFolder || moveCertToFolder(folder.uuid)"
                        >
                            <span v-if="movingToFolder === folder.uuid" class="spinner-border spinner-border-sm text-primary flex-shrink-0" role="status"></span>
                            <img v-else :src="iconFolder" alt="" style="width:14px;height:14px;flex-shrink:0;filter:brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)">
                            {{ folder.name }}
                        </div>
                        <div
                            v-if="moveTargetCert && moveTargetCert.cert_folder_uuid"
                            class="move-folder-item move-folder-item--root"
                            @click="movingToFolder || moveCertToFolder(null)"
                        >
                            <span v-if="movingToFolder === 'root'" class="spinner-border spinner-border-sm text-primary me-2 flex-shrink-0" role="status"></span>
                            <i v-else class="bi bi-house me-2"></i>{{ $t('cert.moveToRoot') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sekce Složky + breadcrumb -->
        <div class="dashboard-section-header mb-3">
            <div class="d-flex align-items-center gap-1 flex-wrap folder-breadcrumb">
                <img :src="iconFolder" alt="" class="dashboard-section-header__icon me-1" style="width:14px;height:14px;filter:brightness(0) invert(88%) sepia(6%) saturate(362%) hue-rotate(202deg) brightness(93%) contrast(87%)">
                <span v-if="!folderPath.length" class="dashboard-section-header__title">{{ $t('cert.myFolders') }}</span>
                <template v-else>
                    <button class="folder-breadcrumb__link" @click="navigateTo(-1)">{{ $t('cert.myFolders') }}</button>
                    <template v-for="(f, i) in folderPath" :key="f.uuid">
                        <i class="bi bi-chevron-right folder-breadcrumb__sep"></i>
                        <button v-if="i < folderPath.length - 1" class="folder-breadcrumb__link" @click="navigateTo(i)">{{ f.name }}</button>
                        <span v-else class="dashboard-section-header__title">{{ f.name }}</span>
                    </template>
                </template>
                <span class="count-badge ms-1">{{ folders.length }}</span>
            </div>
            <button v-if="folders.length > 3 && !showAllFolders" type="button" class="dashboard-show-all" @click="showAllFolders = true">
                {{ $t('cert.viewAll') }}
                <i class="bi bi-chevron-right dashboard-show-all__chevron"></i>
            </button>
        </div>

        <!-- Složky jako karty -->
        <div v-if="!foldersLoading && folders.length > 0" class="folder-cards-row mb-5">
            <div
                v-for="folder in visibleFolders"
                :key="folder.uuid"
                class="folder-card dashboard-action-box--clickable"
                @click="selectFolder(folder)"
            >
                <div class="folder-card__icon">
                    <img :src="iconFolder" alt="" style="filter:brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)">
                </div>
                <p class="folder-card__name">{{ folder.name }}</p>
                <p class="folder-card__count">&nbsp;</p>
                <button
                    class="folder-card__delete-btn"
                    @click.stop="deleteFolder(folder)"
                    :title="$t('file.deleteFolder')"
                    :disabled="deletingFolder === folder.uuid"
                >
                    <span v-if="deletingFolder === folder.uuid" class="spinner-border spinner-border-sm" role="status"></span>
                    <i v-else class="bi bi-trash3"></i>
                </button>
            </div>
        </div>

        <!-- Empty state složky -->
        <div v-if="!foldersLoading && folders.length === 0" class="d-flex justify-content-center mt-4 mb-5">
            <EmptyStateComponent
                :icon="iconFolder"
                :icon-style="{ width: '17px', height: '17px' }"
                :title="$t('cert.emptyFoldersTitle')"
                :text="$t('cert.emptyFoldersText')"
            >
                <button class="btn certs-add-folder-btn" @click="showAddFolderModal = true">
                    {{ $t('cert.addFolder') }}
                    <img :src="iconFolder" alt="" class="certs-add-folder-btn__icon">
                </button>
            </EmptyStateComponent>
        </div>

        <!-- Sekce certifikáty -->
        <div class="dashboard-section-header mb-3 mt-5">
            <div class="d-flex align-items-center gap-2">
                <img :src="iconSpecialOk" alt="" class="dashboard-section-header__icon">
                <span class="dashboard-section-header__title">{{ certsSectionTitle }}</span>
                <span class="count-badge ms-2">{{ totalCount }}</span>
            </div>
            <button v-if="selectedFolder" class="btn cert-list__view-btn" @click="navigateTo(-1)">
                <i class="bi bi-chevron-left me-1"></i>{{ $t('cert.allCertificates') }}
            </button>
        </div>

        <!-- Toolbar řádek 1: vyhledávání + řazení -->
        <div class="certs-toolbar mb-2">
            <div class="certs-search">
                <img :src="iconMagnifying" alt="" class="certs-search__icon">
                <input
                    type="text"
                    class="certs-search__input"
                    v-model="searchQuery"
                    :placeholder="$t('cert.searchPlaceholder')"
                >
            </div>
            <div class="certs-sort">
                <button
                    class="certs-sort__btn"
                    :class="{ 'certs-sort__btn--active': sortColumn === 'name' }"
                    @click="toggleSort('name')"
                >
                    {{ $t('cert.sortByName') }}
                    <i class="bi" :class="sortColumn === 'name' ? (sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-down-up'"></i>
                </button>
                <button
                    class="certs-sort__btn"
                    :class="{ 'certs-sort__btn--active': sortColumn === 'created_at' }"
                    @click="toggleSort('created_at')"
                >
                    {{ $t('cert.sortByDate') }}
                    <i class="bi" :class="sortColumn === 'created_at' ? (sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-down-up'"></i>
                </button>
            </div>
        </div>

        <!-- Toolbar řádek 2: filtry stavu + typ souboru -->
        <div class="certs-toolbar mb-3">
            <div class="certs-filters">
                <button class="certs-filter" :class="{ 'certs-filter--active': activeFilter === 'all' && !selectedFolder }" @click="setFilter('all')">{{ $t('cert.filterAll') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': activeFilter === 'shared' && !selectedFolder }" @click="setFilter('shared')">{{ $t('cert.filterShared') }}</button>
            </div>
            <div class="certs-filters">
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === '' && !!selectedFolder }" @click="fileTypeFilter = ''">{{ $t('cert.filterTypeAll') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'document' }" @click="fileTypeFilter = 'document'">{{ $t('cert.filterTypeDocument') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'photo' }" @click="fileTypeFilter = 'photo'">{{ $t('cert.filterTypePhoto') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'video' }" @click="fileTypeFilter = 'video'">{{ $t('cert.filterTypeVideo') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'audio' }" @click="fileTypeFilter = 'audio'">{{ $t('cert.filterTypeAudio') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'archive' }" @click="fileTypeFilter = 'archive'">{{ $t('cert.filterTypeArchive') }}</button>
                <button class="certs-filter" :class="{ 'certs-filter--active': fileTypeFilter === 'other' }" @click="fileTypeFilter = 'other'">{{ $t('cert.filterTypeOther') }}</button>
            </div>
        </div>

        <Skeletor v-if="loading" />

        <!-- Empty state — certifikáty existují ale filtr nic nenašel -->
        <div v-if="!loading && certsList.length === 0 && (baseCount > 0 || selectedFolder)" class="d-flex justify-content-center mt-5 pt-2">
            <EmptyStateComponent
                :icon="iconSpecialOk"
                :icon-style="{ width: '17px', height: '17px', filter: 'brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)' }"
                :title="$t('cert.noFilterResults')"
                :text="$t('cert.noFilterResultsText')"
            />
        </div>

        <div v-if="!loading && certsList.length" class="cert-list cert-list--certs-mobile">
            <div
                class="cert-list__row"
                v-for="cert in certsList"
                :key="cert.uuid"
            >
                <div class="cert-list__icon-wrap">
                    <i :class="['bi', certFileIcon(cert).cls, 'cert-list__file-icon']" :style="{ color: certFileIcon(cert).color }"></i>
                </div>

                <div class="cert-list__col">
                    <span class="cert-list__label">{{ $t('cert.certName') }}</span>
                    <span class="cert-list__value">{{ cert.name }}</span>
                </div>

                <div class="cert-list__col cert-list__col--date">
                    <span class="cert-list__label">{{ $t('cert.created') }}</span>
                    <span class="cert-list__value">{{ formatDateShort(cert.created_at) }}, {{ formatTime(cert.created_at) }}</span>
                </div>

                <div class="cert-list__action">
                    <span
                        v-if="['link', 'logged'].includes(cert.file?.sharing_settings?.mode)"
                        class="cert-list__sharing-icon d-none d-md-inline-block"
                        :style="{ maskImage: `url(${iconSharingChain})`, webkitMaskImage: `url(${iconSharingChain})` }"
                    ></span>
                    <button
                        class="btn cert-list__view-btn cert-list__view-btn--icon"
                        :title="$t('cert.moveToFolder')"
                        @click="openMoveModal(cert)"
                    >
                        <i class="bi bi-folder-symlink"></i>
                    </button>
                    <button
                        class="btn cert-list__view-btn cert-list__view-btn--icon"
                        :title="$t('cert.certDownload')"
                        :disabled="downloadingCert === cert.uuid"
                        @click="downloadCert(cert, $event)"
                    >
                        <span v-if="downloadingCert === cert.uuid" class="spinner-border spinner-border-sm" role="status"></span>
                        <i v-else class="bi bi-download"></i>
                    </button>
                    <router-link
                        class="btn cert-list__view-btn cert-list__view-btn--icon"
                        :title="$t('cert.viewCertificate')"
                        :to="{ name: 'CertDetail', params: { uuid: cert.uuid } }"
                    >
                        <i class="bi bi-eye"></i>
                    </router-link>
                </div>

                <!-- Mobil: ikona sdílení na posledním řádku -->
                <span
                    v-if="['link', 'logged'].includes(cert.file?.sharing_settings?.mode)"
                    class="cert-list__sharing-icon cert-list__sharing-icon--footer d-md-none"
                    :style="{ maskImage: `url(${iconSharingChain})`, webkitMaskImage: `url(${iconSharingChain})` }"
                ></span>
            </div>
        </div>

        <!-- Stránkování -->
        <div v-if="totalPages > 1" class="certs-pagination mb-5 d-flex align-items-center justify-content-center mt-5">
            <button
                class="certs-pagination__btn"
                :disabled="currentPage <= 1"
                @click="goToPage(currentPage - 1)"
            >
                <i class="bi bi-chevron-left"></i>
            </button>

            <span class="certs-pagination__label">
                {{ $t('cert.pageOf', { current: currentPage, total: totalPages }) }}
            </span>

            <button
                class="certs-pagination__btn"
                :disabled="currentPage >= totalPages"
                @click="goToPage(currentPage + 1)"
            >
                <i class="bi bi-chevron-right"></i>
            </button>
        </div>

        </template><!-- end: !showFullEmpty -->

    </div>
</template>

<script>
import http from '@/http';
import { loadingState } from '@/loading.js';
import iconSpecialOk from '/public/img/new-design/icons/special-ok-icon.svg';
import iconMagnifying from '/public/img/new-design/icons/magnifying.svg';
import iconFolder from '/public/img/new-design/icons/folder.svg';
import iconSharingChain from '/public/img/new-design/icons/sharing.svg';
import iconPlus from '/public/img/new-design/icons/plus.svg';

export default {
    mounted() {
        if (this.$route.query.filter) {
            this.activeFilter = this.$route.query.filter;
        }
        this.fetchItems();
        this.emitter.on('certAdded', (newCert) => {
            this.certsList.unshift(newCert);
            this.totalCount++;
        });
    },

    watch: {
        activeFilter()    { this.currentPage = 1; this.fetchItems(); },
        fileTypeFilter()  { this.currentPage = 1; this.fetchItems(); },
        sortColumn()      { this.currentPage = 1; this.fetchItems(); },
        sortDirection()   { this.currentPage = 1; this.fetchItems(); },
        searchQuery() {
            clearTimeout(this.searchDebounce);
            this.searchDebounce = setTimeout(() => {
                this.currentPage = 1;
                this.fetchItems();
            }, 400);
        },
    },

    data() {
        return {
            // Folder navigation
            folders: [],
            folderPath: [],
            selectedFolder: null,
            foldersLoading: true,
            showAddFolderModal: false,
            showAllFolders: false,
            newFolderName: '',
            creatingFolder: false,
            deletingFolder: null,
            // Move cert
            showMoveModal: false,
            moveTargetCert: null,
            moveFolders: [],
            moveFoldersLoading: false,
            movingToFolder: null,
            // Certs list
            certsList: [],
            loading: true,
            activeFilter: 'all',
            fileTypeFilter: '',
            searchQuery: '',
            sortColumn: 'created_at',
            sortDirection: 'desc',
            searchDebounce: null,
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            baseCount: null,
            perPage: 10,
            downloadingCert: null,
            // Icons
            iconSpecialOk,
            iconMagnifying,
            iconFolder,
            iconSharingChain,
            iconPlus,
        };
    },

    computed: {
        visibleFolders() {
            return this.showAllFolders ? this.folders : this.folders.slice(0, 4);
        },

        certsSectionTitle() {
            return this.selectedFolder
                ? this.$t('cert.certsInFolder') + ' ' + this.selectedFolder.name
                : this.$t('cert.createdCertificates');
        },

        showFullEmpty() {
            return !this.loading && !this.foldersLoading && !this.selectedFolder &&
                   this.certsList.length === 0 && this.folders.length === 0 &&
                   this.baseCount === 0;
        },
    },

    methods: {
        certFileIcon(cert) {
            const mime = cert.file?.mime_type ?? '';
            const ext = (cert.file?.extension || (cert.file?.filename ?? '').split('.').pop() || '').toLowerCase();
            if (mime === 'application/pdf' || ext === 'pdf')
                return { cls: 'bi-file-earmark-pdf', color: '#e74c3c' };
            if (mime.startsWith('image/') || ['jpg','jpeg','png','gif','webp','heic','heif','bmp','tiff','svg'].includes(ext))
                return { cls: 'bi-file-earmark-image', color: '#3498db' };
            if (mime.includes('wordprocessingml') || mime === 'application/msword' || ['doc','docx','odt'].includes(ext))
                return { cls: 'bi-file-earmark-word', color: '#2b579a' };
            if (mime.includes('spreadsheetml') || mime === 'application/vnd.ms-excel' || ['xls','xlsx','ods','csv'].includes(ext))
                return { cls: 'bi-file-earmark-excel', color: '#217346' };
            if (mime.includes('presentationml') || mime === 'application/vnd.ms-powerpoint' || ['ppt','pptx','odp'].includes(ext))
                return { cls: 'bi-file-earmark-ppt', color: '#d24726' };
            if (mime.startsWith('audio/') || ['mp3','wav','ogg','flac','aac','m4a'].includes(ext))
                return { cls: 'bi-file-earmark-music', color: '#1abc9c' };
            if (mime.startsWith('video/') || ['mp4','avi','mov','mkv','webm','m4v'].includes(ext))
                return { cls: 'bi-file-earmark-play', color: '#e67e22' };
            if (['zip','rar','7z','tar','gz','bz2'].includes(ext) || mime.includes('zip'))
                return { cls: 'bi-file-earmark-zip', color: '#8e44ad' };
            if (mime.startsWith('text/') || ['txt','md','rtf'].includes(ext))
                return { cls: 'bi-file-earmark-text', color: '#7f8c8d' };
            if (['php','js','ts','py','html','css','json','xml','sql'].includes(ext))
                return { cls: 'bi-file-earmark-code', color: '#27ae60' };
            return { cls: 'bi-file-earmark', color: '#6B7280' };
        },

        toggleSort(column) {
            if (this.sortColumn === column) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortColumn = column;
                this.sortDirection = column === 'name' ? 'asc' : 'desc';
            }
        },

        setFilter(value) {
            this.activeFilter   = value;
            this.selectedFolder = null;
            this.folderPath     = [];
            this.currentPage    = 1;
            this.fetchItems();
        },

        fetchItems() {
            this.loading = true;
            this.foldersLoading = true;

            const uuid = this.selectedFolder?.uuid;
            const url  = uuid ? `/certs/cert-folders/${uuid}` : '/certs/cert-folders';
            const params = {
                perPage:        this.perPage,
                page:           this.currentPage,
                orderColumn:    this.sortColumn,
                orderDirection: this.sortDirection,
            };
            if (!uuid) params.allFolders = 1;
            if (this.activeFilter !== 'all') params.filter = this.activeFilter;
            if (this.fileTypeFilter)          params.fileType = this.fileTypeFilter;
            if (this.searchQuery.trim())       params.name = this.searchQuery.trim();

            http.get(url, { params }).then((response) => {
                this.folders    = response.data.folders || [];
                this.certsList  = response.data.certs   || [];
                this.totalCount = response.data.certs_total ?? this.certsList.length;
                this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.perPage));

                if (!params.name && !params.fileType && this.activeFilter === 'all') {
                    this.baseCount = this.totalCount;
                }

                this.loading       = false;
                this.foldersLoading = false;
                loadingState.active = false;
            });
        },

        selectFolder(folder) {
            if (this.selectedFolder?.uuid === folder.uuid) return;
            this.folderPath.push(folder);
            this.selectedFolder = folder;
            this.currentPage    = 1;
            this.certsList      = [];
            this.showAllFolders = false;
            this.fetchItems();
        },

        navigateTo(index) {
            if (index === -1) {
                this.selectedFolder = null;
                this.folderPath     = [];
            } else {
                this.selectedFolder = this.folderPath[index];
                this.folderPath     = this.folderPath.slice(0, index + 1);
            }
            this.currentPage    = 1;
            this.certsList      = [];
            this.showAllFolders = false;
            this.fetchItems();
        },

        createFolder() {
            if (!this.newFolderName || this.creatingFolder) return;
            this.creatingFolder = true;
            const payload = { name: this.newFolderName };
            if (this.selectedFolder) payload.uuid = this.selectedFolder.uuid;
            http.post('/certs/cert-folders/create', payload).then((response) => {
                if (response.data.folder) {
                    this.folders.unshift(response.data.folder);
                }
                this.showAddFolderModal = false;
                this.newFolderName = '';
            }).finally(() => {
                this.creatingFolder = false;
            });
        },

        deleteFolder(folder) {
            const count = folder.children_count || 0;
            const msg   = count > 0
                ? this.$t('cert.confirmDeleteFolderWithCerts', { count })
                : this.$t('cert.confirmDeleteFolder');

            this.$swal.fire({
                title: msg,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#6c757d',
                confirmButtonText: this.$t('general.yes'),
                cancelButtonText: this.$t('general.no'),
            }).then((result) => {
                if (result.isConfirmed) {
                    this.deletingFolder = folder.uuid;
                    http.post(`/certs/cert-folders/${folder.uuid}/delete`).then(() => {
                        this.folders = this.folders.filter(f => f.uuid !== folder.uuid);
                    }).finally(() => {
                        this.deletingFolder = null;
                    });
                }
            });
        },

        openMoveModal(cert) {
            this.moveTargetCert    = cert;
            this.showMoveModal     = true;
            this.moveFoldersLoading = true;
            http.get('/certs/cert-folders/all').then((response) => {
                this.moveFolders = response.data.folders || [];
            }).catch(() => {
                this.moveFolders = [];
            }).finally(() => {
                this.moveFoldersLoading = false;
            });
        },

        moveCertToFolder(folderUuid) {
            const key = folderUuid ?? 'root';
            this.movingToFolder = key;
            http.post(`/certs/${this.moveTargetCert.uuid}/move`, { folder_uuid: folderUuid }).then(() => {
                this.certsList = this.certsList.filter(c => c.uuid !== this.moveTargetCert.uuid);
                this.totalCount = Math.max(0, this.totalCount - 1);
                this.showMoveModal  = false;
                this.moveTargetCert = null;
            }).finally(() => {
                this.movingToFolder = null;
            });
        },

        goToPage(page) {
            if (page < 1 || page > this.totalPages) return;
            this.currentPage = page;
            this.fetchItems();
        },

        downloadCert(cert, event) {
            event?.currentTarget?.blur();
            this.downloadingCert = cert.uuid;
            http.post('/certs/' + cert.uuid + '/download').then((response) => {
                const fileURL = `data:application/pdf;base64,${response.data}`;
                const link    = document.createElement('a');
                link.href     = fileURL;
                link.setAttribute('download', (cert.name || 'Certificate') + '.pdf');
                document.body.appendChild(link);
                link.click();
                link.remove();
            }).finally(() => {
                this.downloadingCert = null;
            });
        },

        formatDateShort(dateStr) {
            const d    = new Date(dateStr);
            const days = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
            return `${days[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
        },

        formatTime(dateStr) {
            const d   = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
    },
};
</script>

<style scoped>
.folder-card {
    position: relative;
}

.folder-card__delete-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    background: none;
    border: none;
    padding: 4px 6px;
    color: #d33;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    border-radius: 4px;
    display: none;
}
.folder-card:hover .folder-card__delete-btn {
    display: block;
}
.folder-card__delete-btn:hover {
    background: #fff0f0;
}

.move-folder-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    color: #1a1a2e;
    border: 1px solid transparent;
    margin-bottom: 4px;
    transition: background .15s;
}
.move-folder-item:hover {
    background: #f0f5ff;
    border-color: #c7d9ff;
}
.move-folder-item--root {
    margin-top: 8px;
    color: #6b7280;
    border-top: 1px solid #e5e7eb;
    border-radius: 0;
    padding-top: 12px;
}
</style>
